import React, { forwardRef } from "react";

import { cva, type VariantProps } from "class-variance-authority";

import type { IconProps } from "@eisox/icons";
import type { colors } from "@eisox/tokens";

import { Circle } from "../Circle";

const overviewCardVariants = cva("md:justify-center flex w-fit items-center justify-start rounded-[10px] select-none", {
  variants: {
    variants: {
      default: "bg-primary-white",
      info: "bg-primary-gray",
      success: "bg-primary-green",
      danger: "bg-primary-red",
    },
    size: {
      small: "md:p-[20px] gap-[10px] p-[10px]",
      large: "md:gap-[20px] md:p-[40px] gap-[10px] p-[10px]",
    },
  },
  defaultVariants: {
    variants: "default",
    size: "small",
  },
});

const iconVariants = cva("", {
  variants: {
    size: {
      small: "md:w-[50px]! md:h-[50px]! h-[42px]! w-[42px]!",
      large: "md:w-[70px]! md:h-[70px]! h-[42px]! w-[42px]!",
    },
  },
  defaultVariants: { size: "small" },
});

const titleVariants = cva(
  "font-semibold md:text-2xl [&_span]:font-light flex items-center gap-[4px] text-base whitespace-nowrap [&_span]:self-start [&_span]:text-base",
  {
    variants: {
      variants: {
        default: "text-primary-dark-blue [&_span]:text-primary-dark-gray",
        info: "text-primary-dark-blue [&_span]:text-primary-dark-gray",
        success: "text-primary-white",
        danger: "text-primary-white",
      },
    },
    defaultVariants: { variants: "default" },
  },
);

const subtitleVariants = cva("font-light md:text-base text-sm", {
  variants: {
    variants: {
      default: "text-primary-dark-gray",
      info: "text-primary-dark-gray",
      success: "text-primary-white",
      danger: "text-primary-white",
    },
  },
  defaultVariants: { variants: "default" },
});

interface OverviewCardProps extends React.ComponentPropsWithRef<"div">, VariantProps<typeof overviewCardVariants> {
  icon: React.ReactElement<IconProps & React.RefAttributes<SVGSVGElement>>;
  iconBackgroundColor?: keyof typeof colors.primary;
  color?: keyof typeof colors.primary;
  title: string;
  subtitle: React.ReactNode;
  hasDegre?: boolean;
}

export const OverviewCard = forwardRef<HTMLDivElement, OverviewCardProps>((props, forwardedRef) => {
  const {
    icon,
    iconBackgroundColor = "white",
    color = "darkBlue",
    title,
    subtitle,
    hasDegre,
    variants,
    size,
    className,
    children,
    onClick,
    style,
    ...overviewCardProps
  } = props;

  return (
    <div
      {...overviewCardProps}
      ref={forwardedRef}
      className={overviewCardVariants({ variants, size, className })}
      onClick={onClick}
      style={{ ...style, cursor: onClick ? "pointer" : "default" }}
    >
      <Circle.Root className={iconVariants({ size })} color={color} backgroundColor={iconBackgroundColor}>
        {icon}
      </Circle.Root>
      <div className="flex flex-col items-start gap-[2px]">
        <div className={titleVariants({ variants })}>
          {title} {hasDegre && <span>°C</span>} {children}
        </div>
        <div className={subtitleVariants({ variants })}>{subtitle}</div>
      </div>
    </div>
  );
});

OverviewCard.displayName = "OverviewCard";
